import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Stack, Button, Chip, Alert, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material'
import { formatDistanceToNow } from 'date-fns'

import { getAllVisits } from '../lib/idbUtils'
import { runSync, runPoll } from '../../../sync/SyncEngine'

const STUCK_THRESHOLD_MS = 60 * 60 * 1000

function SyncStatusPanel({ isOnline, enabledVisitHost }) {
  const { t } = useTranslation()
  const [visits, setVisits] = useState([])
  const [loading, setLoading] = useState(false)
  const [actionRunning, setActionRunning] = useState(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      setVisits(await getAllVisits())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
    window.addEventListener('lobby:sync-complete', refresh)
    return () => window.removeEventListener('lobby:sync-complete', refresh)
  }, [refresh])

  const counts = useMemo(() => visits.reduce((acc, v) => {
    const key = v.syncStatus || 'unknown'
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, { pending: 0, synced: 0, failed: 0 }), [visits])

  const pendingVisits = useMemo(() => visits.filter(v => v.syncStatus === 'pending'), [visits])
  const failedVisits = useMemo(() => visits.filter(v => v.syncStatus === 'failed'), [visits])

  const stuckPendingCount = useMemo(() => {
    if (!isOnline) return 0
    const now = Date.now()
    return pendingVisits.filter(v => v.entry_time && (now - new Date(v.entry_time).getTime()) > STUCK_THRESHOLD_MS).length
  }, [pendingVisits, isOnline])

  const runAction = useCallback(async (actionId, fn) => {
    setActionRunning(actionId)
    try {
      await fn({ syncEmployees: !!enabledVisitHost })
      await refresh()
    } finally {
      setActionRunning(null)
    }
  }, [enabledVisitHost, refresh])

  const drillDown = [...failedVisits, ...pendingVisits]

  return (
    <Stack spacing={3} className="audit-section">
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <h3>{t('audit_sync_status_title')}</h3>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={refresh} disabled={loading}>
            {t('audit_refresh')}
          </Button>
          <Button
            variant="contained"
            disabled={!isOnline || actionRunning !== null}
            onClick={() => runAction('sync', runSync)}
          >
            {actionRunning === 'sync' ? t('audit_syncing') : t('audit_force_full_sync')}
          </Button>
          <Button
            variant="outlined"
            disabled={!isOnline || actionRunning !== null}
            onClick={() => runAction('poll', runPoll)}
          >
            {actionRunning === 'poll' ? t('audit_polling') : t('audit_force_poll')}
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Chip label={`${t('sync_pending')}: ${counts.pending}`} color={counts.pending ? 'warning' : 'default'} />
        <Chip label={`${t('sync_done')}: ${counts.synced}`} color="success" />
        <Chip label={`${t('sync_failed')}: ${counts.failed}`} color={counts.failed ? 'error' : 'default'} />
      </Stack>

      {counts.failed > 0 && (
        <Alert severity="error">{t('audit_failed_visits_alert', { count: counts.failed })}</Alert>
      )}
      {stuckPendingCount > 0 && (
        <Alert severity="warning">{t('audit_stuck_pending_alert', { count: stuckPendingCount })}</Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('sync_status')}</TableCell>
            <TableCell>{t('audit_uuid')}</TableCell>
            <TableCell>{t('Identification')}</TableCell>
            <TableCell>{t('audit_entry_time')}</TableCell>
            <TableCell>{t('audit_building_id')}</TableCell>
            <TableCell>{t('audit_elapsed')}</TableCell>
            <TableCell>{t('audit_sync_error_reason')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {drillDown.map(v => (
            <TableRow key={v.uuid}>
              <TableCell>
                <Chip
                  size="small"
                  label={v.syncStatus}
                  color={v.syncStatus === 'failed' ? 'error' : 'warning'}
                />
              </TableCell>
              <TableCell>{v.uuid}</TableCell>
              <TableCell>{v.identification}</TableCell>
              <TableCell>{v.entry_time}</TableCell>
              <TableCell>{v.building_id}</TableCell>
              <TableCell>{v.entry_time ? formatDistanceToNow(new Date(v.entry_time), { addSuffix: true }) : '—'}</TableCell>
              <TableCell>{v.syncError || '—'}</TableCell>
            </TableRow>
          ))}
          {drillDown.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>{t('audit_no_pending_failed_visits')}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Stack>
  )
}

const mapStateToProps = state => ({
  isOnline: state.utilities.isOnline,
  enabledVisitHost: state.profile.enabled_visit_host,
})

export default connect(mapStateToProps)(SyncStatusPanel)
